import { getBalanceSheet, getCashFlow, getProfitAndLoss } from "../../apiClient";
import { formatDateForAPI } from "../dateUtils";
import { sumCashFlowByCategory } from "../collectionUtils";
import { pickDimensionFilters, pickDimensionQueryParams } from "../dimensionParams";
import { formatReportApiError, isRequestCancelled } from "../errorUtils";
import { calculateWorkingCapital } from "../liquidityMetrics";
import { formatBalanceSheetData } from "../formatters/balanceSheet";
import { getMockReportData } from "../mockData";
import { calculateGrossMargin, calculateOperatingMargin } from "../profitabilityMetrics";
import {
  buildGeneralSnapshot,
  generateAlerts,
  generateRecommendations,
  getPercentageStatus,
  getRatioStatus,
  projectNextPeriod,
} from "./generalInsights";
import { formatCashFlowData } from "./cashFlow";
import { formatVerticalIncomeData } from "./profitAndLoss";

const formatGeneralData = (balanceData, cashFlowData, pnlData) => {
  const snapshot = buildGeneralSnapshot(balanceData, cashFlowData, pnlData);
  const profitAndLoss = formatVerticalIncomeData(pnlData);
  const netIncome = snapshot.totalRevenue - snapshot.totalExpenses;
  const netMargin = profitAndLoss.ratios?.netMargin || 0;

  return {
    comprehensiveSummary: {
      financialPosition: {
        totalAssets: snapshot.totalAssets,
        totalLiabilities: snapshot.totalLiabilities,
        totalEquity: snapshot.totalEquity,
        workingCapital: calculateWorkingCapital(balanceData),
        currentRatio: snapshot.currentRatio,
        quickRatio: snapshot.quickRatio,
      },
      profitability: {
        totalRevenue: snapshot.totalRevenue,
        totalExpenses: snapshot.totalExpenses,
        netIncome,
        grossMargin: calculateGrossMargin(pnlData),
        operatingMargin: calculateOperatingMargin(pnlData),
        netMargin,
        returnOnAssets: snapshot.totalAssets > 0 ? (netIncome / snapshot.totalAssets) * 100 : 0,
        returnOnEquity: snapshot.totalEquity > 0 ? (netIncome / snapshot.totalEquity) * 100 : 0,
      },
      cashFlow: {
        operatingCashFlow: snapshot.operatingCashFlow,
        investingCashFlow: sumCashFlowByCategory(cashFlowData.investing || []),
        financingCashFlow: sumCashFlowByCategory(cashFlowData.financing || []),
        netCashFlow: snapshot.netCashFlow,
        freeCashFlow: snapshot.freeCashFlow,
      },
      efficiency: {
        assetTurnover: snapshot.totalAssets > 0 ? snapshot.totalRevenue / snapshot.totalAssets : 0,
        daysWorkingCapital: snapshot.daysWorkingCapital,
        defensiveInterval: snapshot.defensiveInterval,
        expenseToRevenue: snapshot.totalRevenue > 0 ? (snapshot.totalExpenses / snapshot.totalRevenue) * 100 : 0,
      },
    },
    detailedReports: {
      balanceSheet: formatBalanceSheetData(balanceData),
      cashFlow: formatCashFlowData(cashFlowData),
      profitAndLoss,
    },
    comparativeAnalysis: {
      liquidity: {
        currentRatio: { value: snapshot.currentRatio, status: getRatioStatus(snapshot.currentRatio, 2, 1.5) },
        quickRatio: { value: snapshot.quickRatio, status: getRatioStatus(snapshot.quickRatio, 1, 0.8) },
      },
      profitability: {
        grossMargin: { value: calculateGrossMargin(pnlData), status: getPercentageStatus(calculateGrossMargin(pnlData), 40, 30) },
        netMargin: { value: netMargin, status: getPercentageStatus(netMargin, 15, 10) },
      },
    },
    immediateAlerts: generateAlerts(balanceData, cashFlowData, pnlData),
    outlook: {
      nextPeriodProjection: projectNextPeriod(balanceData, cashFlowData, pnlData),
      recommendations: generateRecommendations(balanceData, cashFlowData, pnlData),
    },
  };
};

export const handleGeneralReport = async (
  filters,
  { balanceDate, rangeEndDate, rangeStartDate },
  language = "ar",
  options = {}
) => {
  try {
    const [balanceResponse, cashFlowResponse, pnlResponse] = await Promise.all([
      getBalanceSheet({
        date: balanceDate,
        currency: filters.currency,
        period_count: filters.period_count || 0,
        ...pickDimensionQueryParams(filters),
      }, { signal: options.signal }),
      getCashFlow({
        currency: filters.currency,
        date_after: formatDateForAPI(rangeStartDate),
        date_before: formatDateForAPI(rangeEndDate),
        compare_period_unit: filters.compare_period_unit || "month",
        ...(filters.cash_flow_type && { cash_flow_type: filters.cash_flow_type }),
        ...pickDimensionFilters(filters),
      }, { signal: options.signal }),
      getProfitAndLoss({
        currency: filters.currency,
        date_after: formatDateForAPI(rangeStartDate),
        date_before: formatDateForAPI(rangeEndDate),
        compare_period_unit: filters.compare_period_unit || "month",
        ...(filters.category && { category: filters.category }),
        ...pickDimensionFilters(filters),
      }, { signal: options.signal }),
    ]);

    return {
      success: true,
      reportType: "general",
      isRealData: true,
      originalData: {
        balanceSheet: balanceResponse.data,
        cashFlow: cashFlowResponse.data,
        profitAndLoss: pnlResponse.data,
      },
      data: formatGeneralData(balanceResponse.data, cashFlowResponse.data, pnlResponse.data),
    };
  } catch (error) {
    if (isRequestCancelled(error)) throw error;
    console.error("General Report API Error:", error);
    return {
      success: false,
      reportType: "general",
      isRealData: false,
      data: getMockReportData("general", filters),
      error: formatReportApiError(error, language),
    };
  }
};
